import { FormApi } from "final-form";
import React from "react";
import { Form, useField } from "react-final-form";
import { useTranslation } from "react-i18next";
import { CustomBadge } from "src/ui/shared/CustomBadge";
import { DangerOutlinedButton, PrimaryOutlinedButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import { Modal } from "src/ui/shared/Modal/Modal";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableHeader,
    MinimalTableHeaderCell,
} from "src/ui/shared/table/MinimalTableComponents";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";

import { AndroidProject } from "@dashboard/products/types";
import { Box, Typography } from "@mui/material";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { MinimalTableBody, MinimalTableContainer, MinimalTableRow } from "@shared/table/MinimalTableComponents";

import { actionOptions, projectValidationSchema } from "./ProjectSection.utils";

const useStyles = makeStyles()((theme) => ({
    requiredBox: {
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    nonRequiredBox: { marginTop: theme.spacing(3), display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    buttonBox: { marginBottom: theme.spacing(3), marginTop: theme.spacing(2), display: "flex", justifyContent: "flex-end" },
}));

type FormValues = AndroidProject;

type Props = {
    isCreationDisabled: boolean;
    creationLockTooltip: string;
};

export const ProjectSection = ({ isCreationDisabled, creationLockTooltip }: Props) => {
    const [open, setOpen] = React.useState(false);

    const { input: projectsInput } = useField<AndroidProject[]>("customBuildConfig.manifest.projects");

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common"]);
    const validate = useValidationSchema(projectValidationSchema);

    const onRemove = (project: AndroidProject) => {
        const newProjects = (projectsInput.value || []).filter((item) => item.name !== project.name);
        projectsInput.onChange(newProjects);
    };

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        projectsInput.onChange([...(projectsInput.value || []), values]);
        form.restart();
    };

    return (
        <React.Fragment>
            <CustomBadge badgeContent={(projectsInput.value || []).length}>
                <SecondaryButton
                    onClick={() => setOpen(true)}
                    startIcon={<i className="fa-solid fa-code-branch" />}
                    tooltipProps={{ title: t("pages:productPage.tooltip.manageProjects") }}
                >
                    {t("pages:productPage.manageProjects")}
                </SecondaryButton>
            </CustomBadge>

            <Modal
                open={open}
                maxWidth="md"
                onClose={() => setOpen(false)}
                title={t("modals:productProject.title")}
                description={t("modals:productProject.description")}
            >
                <Form<FormValues>
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={{ action: "add" }}
                    render={({ handleSubmit, values, form }) => (
                        <form onSubmit={handleSubmit}>
                            <Box className={classes.requiredBox}>
                                <ControlledSelectField
                                    name="action"
                                    label={t("common:label.action")}
                                    options={actionOptions}
                                    fullWidth
                                    disabled={isCreationDisabled}
                                />
                                <TextField
                                    name="name"
                                    label={t("modals:productProject.input.name.caption")}
                                    placeholder={t("modals:productProject.input.name.placeholder")}
                                    fullWidth
                                    disabled={isCreationDisabled}
                                />
                            </Box>

                            {values?.action === "add" ? (
                                <Box className={classes.nonRequiredBox}>
                                    <TextField
                                        name="path"
                                        label={t("modals:productProject.input.path.caption")}
                                        placeholder={t("modals:productProject.input.path.placeholder")}
                                        fullWidth
                                        disabled={isCreationDisabled}
                                    />
                                    <TextField
                                        name="remote"
                                        label={t("modals:productProject.input.remote.caption")}
                                        placeholder={t("modals:productProject.input.remote.placeholder")}
                                        fullWidth
                                        disabled={isCreationDisabled}
                                    />
                                    <TextField
                                        name="revision"
                                        label={t("modals:productProject.input.revision.caption")}
                                        placeholder={t("modals:productProject.input.revision.placeholder")}
                                        fullWidth
                                        disabled={isCreationDisabled}
                                    />
                                </Box>
                            ) : null}

                            <Box className={classes.buttonBox}>
                                <PrimaryOutlinedButton
                                    onClick={form.submit}
                                    disabled={isCreationDisabled}
                                    tooltipProps={{
                                        title: creationLockTooltip,
                                        hide: !isCreationDisabled,
                                    }}
                                >
                                    {values?.action === "add" ? t("common:button.add") : t("common:button.delete")}
                                </PrimaryOutlinedButton>
                            </Box>

                            <MinimalTableContainer plain>
                                <MinimalTable>
                                    <MinimalTableHeader>
                                        <MinimalTableRow>
                                            <MinimalTableHeaderCell>
                                                <Typography fontWeight={600}>
                                                    {" "}
                                                    {t("modals:productProject.table.header.name")}
                                                </Typography>
                                            </MinimalTableHeaderCell>
                                            <MinimalTableHeaderCell>
                                                <Typography fontWeight={600}>
                                                    {t("modals:productProject.table.header.status")}
                                                </Typography>
                                            </MinimalTableHeaderCell>
                                            <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                        </MinimalTableRow>
                                    </MinimalTableHeader>
                                    <MinimalTableBody>
                                        {!(projectsInput.value || []).length ? (
                                            <MinimalTableRow>
                                                <MinimalTableCell colSpan={3} align="center">
                                                    <Typography>{t("modals:productProject.table.noItemsAvailable")}</Typography>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ) : null}

                                        {(projectsInput.value || []).map((item) => (
                                            <MinimalTableRow key={item.name}>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.name}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>
                                                        {item.action === "add"
                                                            ? t("modals:productProject.table.label.added")
                                                            : t("modals:productProject.table.label.removed")}
                                                    </Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => onRemove(item)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))}
                                    </MinimalTableBody>
                                </MinimalTable>
                            </MinimalTableContainer>
                        </form>
                    )}
                />
            </Modal>
        </React.Fragment>
    );
};
